import { truncateMarkdown } from './truncateMarkdown';

// Previews show a few clamped lines, no need to process whole articles
// (older blog short descriptions hold the full article).
const PREVIEW_SOURCE_LENGTH = 600;

// Raw html that may stay inside an inline preview (e.g. <u> from the editor's
// underline button); any other tag (<div>, <h2>, <img>, ...) is dropped.
const INLINE_HTML_TAGS = new Set([
  'b',
  'code',
  'del',
  'em',
  'i',
  'ins',
  'mark',
  's',
  'small',
  'strong',
  'sub',
  'sup',
  'u',
]);

/**
 * Flattens markdown to a single line of inline markdown for clamped previews
 * (blog cards): block syntax (headings, lists, quotes, code fences) is dropped,
 * links become their text (cards are links themselves), images and raw block
 * html tags are removed. Inline formatting (bold, italic, strikethrough, <u>)
 * is kept.
 */
export function toInlineMarkdown(markdown: string): string {
  const { text } = truncateMarkdown(
    markdown.replace(/\r\n/g, '\n'),
    PREVIEW_SOURCE_LENGTH
  );

  return (
    text
      .replace(/^\s*(```|~~~).*$/gm, '')
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      .replace(/\[((?:\\.|[^\]\\])*)\]\([^)]*\)/g, '$1')
      .replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1')
      .replace(/<([a-z][a-z0-9+.-]{1,31}:[^\s<>]*)>/gi, '$1')
      .replace(/<([^\s<>@]+@[^\s<>@]+\.[^\s<>@]+)>/g, '$1')
      .replace(/<\/?([a-z][a-z0-9-]*)\b[^>]*>/gi, (tag, name: string) =>
        INLINE_HTML_TAGS.has(name.toLowerCase()) ? tag : ' '
      )
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/^\s{0,3}>\s?/gm, '')
      .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, '')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};

/** Markdown to plain text, e.g. for meta descriptions. */
export function markdownToPlainText(markdown: string): string {
  // Backslash-escaped characters (e.g. link text from the Strapi link dialog)
  // are literal: set them aside so the formatting strip below can't touch them.
  const escaped: string[] = [];
  const protectedText = toInlineMarkdown(markdown).replace(
    /\\([\\`*_{}[\]()#+\-.!<>~|])/g,
    (_, char: string) => `\u0000${escaped.push(char) - 1}\u0000`
  );

  return protectedText
    .replace(/<[^>]+>/g, '')
    .replace(/(\*\*|__|~~)(.+?)\1/g, '$2')
    .replace(/(^|[^\w*])[*_](?!\s)(.+?)(?<!\s)[*_](?![\w*])/g, '$1$2')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (entity) => HTML_ENTITIES[entity])
    .replace(/\u0000(\d+)\u0000/g, (_, index: string) => escaped[Number(index)])
    .replace(/\s+/g, ' ')
    .trim();
}
